import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { PLAN_TERMS } from "../../shared/billing";
import { PASSWORD_MIN_LENGTH } from "../../shared/constants";
import { claimSchema } from "../../shared/schemas";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { Alert, AuthLayout, focusFirstInvalid, TextField } from "../components/ui";
import { apiFailure, type FieldErrors, validate } from "./forms";

// „Preia restaurantul” (owner 2026-11-04): the one-time link Meniova sends a client whose menu it prepared. The
// client creates the account (e-mail and password) or uses the account they are signed in with; the next steps
// (company, card with 0 € today) follow in the app itself.

interface ClaimInfo {
  name: string;
  city: string;
  planId: string;
  trialDays: number;
  payments: boolean;
}

const eur = (minor: number) => `${(minor / 100).toLocaleString("ro-RO", { minimumFractionDigits: 2 })} €`;

export function planLine(planId: string) {
  return planId === PLAN_TERMS.month.id
    ? `Plan lunar · ${eur(PLAN_TERMS.month.priceMinor)} pe lună + TVA`
    : `Plan anual · ${eur(PLAN_TERMS.year.introPriceMinor)} primul an, apoi ${eur(PLAN_TERMS.year.priceMinor)} pe an + TVA`;
}

export function ClaimPage() {
  const { token = "" } = useParams();
  const navigate = useNavigate();
  const { state, refresh } = useAuth();
  const formRef = useRef<HTMLFormElement>(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", acceptTerms: false });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const info = useQuery({
    queryKey: ["claim", token],
    queryFn: () => api<ClaimInfo>("GET", `/auth/claim/${token}`),
    retry: false,
  });

  async function send(body: unknown) {
    setBusy(true);
    setMessage("");
    try {
      await api("POST", `/auth/claim/${token}`, body);
      await refresh();
      navigate("/app", { replace: true });
    } catch (err) {
      const f = apiFailure(err);
      setErrors(f.fields);
      setMessage(f.message);
    } finally {
      setBusy(false);
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const v = validate(claimSchema, form);
    if (!v.ok) {
      setErrors(v.errors);
      focusFirstInvalid(formRef.current);
      return;
    }
    setErrors({});
    await send(form);
  }

  if (info.isPending) {
    return (
      <AuthLayout>
        <p className="muted" role="status">
          Se încarcă…
        </p>
      </AuthLayout>
    );
  }
  if (info.isError) {
    return (
      <AuthLayout>
        <h1 className="h1">Linkul nu mai este valid</h1>
        <p className="muted" style={{ margin: 0 }}>
          A fost folosit deja sau a expirat (e valabil 7 zile). Cere echipei Meniova un link nou.
        </p>
        <p className="auth-foot">
          <Link to="/login">Am deja cont</Link>
        </p>
      </AuthLayout>
    );
  }
  const d = info.data;
  const signedIn = state.status === "authenticated" && !state.me.support;

  return (
    <AuthLayout>
      <div className="stack" style={{ gap: 8 }}>
        <span className="greet">Meniul tău e pregătit</span>
        <h1 className="h1">
          {d.name}
          {d.city ? `, ${d.city}` : ""}
        </h1>
        <p className="muted" style={{ margin: 0 }}>
          Echipa Meniova ți-a pregătit meniul digital. Îți creezi contul, completezi datele firmei și adaugi
          cardul. Azi plătești 0 €: primele {d.trialDays} zile sunt gratuite, apoi abonamentul se reînnoiește
          automat.
        </p>
      </div>
      <div className="claim-plan">
        <strong>{planLine(d.planId)}</strong>
        <span className="caption muted">
          {d.trialDays} zile gratuite de azi. Anulezi oricând înainte de prima plată, fără costuri.
        </span>
      </div>
      {message && (
        <Alert tone="error" live>
          {message}
          {message.includes("Intră în cont") && (
            <>
              {" "}
              <Link to={`/login?next=${encodeURIComponent(`/preluare/${token}`)}`}>Intră în cont</Link>
            </>
          )}
        </Alert>
      )}
      {signedIn ? (
        <div className="stack" style={{ gap: 12 }}>
          <p style={{ margin: 0 }}>
            Ești conectat ca <strong>{state.me.user.email}</strong>. Restaurantul se adaugă la acest cont.
          </p>
          <button
            type="button"
            className="btn btn-primary btn-block"
            disabled={busy}
            onClick={() => send({ useSession: true })}
          >
            {busy ? "Se preia…" : "Preia restaurantul"}
          </button>
        </div>
      ) : (
        <form ref={formRef} onSubmit={onSubmit} noValidate>
          <TextField
            id="claim-name"
            label="Numele tău"
            autoComplete="name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            error={errors.name}
          />
          <TextField
            id="claim-email"
            label="E-mail"
            type="email"
            autoComplete="email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            error={errors.email}
          />
          <TextField
            id="claim-password"
            label={`Parolă (minimum ${PASSWORD_MIN_LENGTH} caractere)`}
            type="password"
            autoComplete="new-password"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            error={errors.password}
          />
          <label className="check" htmlFor="claim-terms">
            <input
              id="claim-terms"
              type="checkbox"
              checked={form.acceptTerms}
              aria-invalid={errors.acceptTerms ? "true" : "false"}
              aria-labelledby="claim-terms-text"
              aria-describedby={errors.acceptTerms ? "claim-terms-error" : undefined}
              onChange={(e) => setForm({ ...form, acceptTerms: e.target.checked })}
            />
            <span id="claim-terms-text">
              Accept <Link to="/termeni">Termenii de utilizare</Link> și am citit{" "}
              <Link to="/confidentialitate">Nota de confidențialitate</Link>.
            </span>
          </label>
          {errors.acceptTerms && (
            <div id="claim-terms-error" className="field-error">
              {errors.acceptTerms}
            </div>
          )}
          <button className="btn btn-primary btn-block" type="submit" disabled={busy}>
            {busy ? "Se creează contul…" : "Creează contul"}
          </button>
        </form>
      )}
      {!signedIn && (
        <p className="auth-foot">
          Ai deja un cont Meniova?{" "}
          <Link to={`/login?next=${encodeURIComponent(`/preluare/${token}`)}`}>Intră în cont</Link>, apoi
          deschide din nou linkul.
        </p>
      )}
    </AuthLayout>
  );
}
